/**
 * The one place that writes to GTM's `window.dataLayer`, and the contract
 * for what each event carries.
 *
 * GTM is loaded from each app's `index.html` (set-edu: prod-set only);
 * where it isn't, pushes just accumulate in an unread `window.dataLayer`.
 * The FIT container (GTM-MDC9K4F9) and the ALX one (GTM-5VTSDQ5) read these
 * names through Data Layer Variables, so renaming an event or a field breaks
 * their triggers silently.
 */

export type LoginMethod = 'email' | 'google' | 'facebook' | 'linkedin' | 'ehub';

export interface ProgramParams {
  program_id: string;
  program_name: string;
  program_abbreviation?: string | null;
}

export interface PlanParams {
  /** `free` for staying on the free tier. */
  plan_id: string;
  plan_name: string;
  billing_interval?: string | null;
  /** In major units (12.5, not 1250). */
  value: number;
  currency?: string;
}

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

/** GA4's recommended ecommerce object. */
export interface EcommerceParams {
  ecommerce: {
    transaction_id: string;
    value: number;
    currency?: string;
    items: {
      item_id: string;
      item_name: string;
      item_variant?: string | null;
      price: number;
      quantity: number;
    }[];
  };
}

interface AuthParams {
  method: LoginMethod;
  user_id: string;
}

interface EnrolledParams extends ProgramParams {
  program_enrollment_id?: string;
  user_id?: string | null;
}

/** Program fields are absent when the learner was not starting a program. */
type PaymentParams = PlanParams & Partial<ProgramParams>;

/**
 * Every event and exactly what it carries. The payment names are the ones
 * the ALX container already listens for, so both brands share them.
 */
export interface AnalyticsEvents {
  user_login: AuthParams;
  user_signed_up: AuthParams;
  referral_signup_page_view: UtmParams;
  referral_signup_submitted: UtmParams;
  program_detail_view: ProgramParams;
  enrollment_started: ProgramParams;
  subscription_plan_selected: PaymentParams;
  payment_initiated: PaymentParams & EcommerceParams;
  payment_successful: PaymentParams & EcommerceParams;
  program_enrolled: EnrolledParams;
  first_program_enrolled: EnrolledParams;
}

export type AnalyticsEventName = keyof AnalyticsEvents;

type EventField = Exclude<
  {
    [E in AnalyticsEventName]: keyof AnalyticsEvents[E];
  }[AnalyticsEventName],
  'user_id' | 'ecommerce'
>;

/**
 * GTM merges every push into one persistent model, and these are single-page
 * apps, so a field from an earlier event (the plan from checkout, the program
 * from a detail page) would ride along on every later one. Each event clears
 * them first. Typed as a Record so a field added to an event above has to be
 * added here too. `user_id` is kept on purpose (see `setDataLayerUser`), and
 * `ecommerce` is cleared separately, the way GA4 documents.
 */
const CLEARED_FIELDS: Record<EventField, undefined> = {
  method: undefined,
  program_id: undefined,
  program_name: undefined,
  program_abbreviation: undefined,
  program_enrollment_id: undefined,
  plan_id: undefined,
  plan_name: undefined,
  billing_interval: undefined,
  value: undefined,
  currency: undefined,
  utm_source: undefined,
  utm_medium: undefined,
  utm_campaign: undefined,
  utm_content: undefined,
  utm_term: undefined,
};

type DataLayerEntry = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
  }
}

const push = (entry: DataLayerEntry) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(entry);
};

export const pushDataLayerEvent = <E extends AnalyticsEventName>(
  event: E,
  payload: AnalyticsEvents[E]
) => {
  if ('ecommerce' in payload) push({ ecommerce: null });
  push({ ...CLEARED_FIELDS, event, ...payload });
};

/**
 * Pushed with no event, so GTM's `user_id` variable holds the learner's
 * sandId for every event after it, page views included.
 */
export const setDataLayerUser = (sandId: string) => push({ user_id: sandId });
